import { gql } from "@apollo/client";

import { METADATA_FIELDS_FRAGMENT } from "~frontend/graphql/campaignMetadata";

export const SUBACCOUNT_BASE_FIELDS = gql`
  fragment SubaccountBaseFields on Subaccount {
    id
    name
    slug
    autoAddRefcodesToABLinks
    archivedAt
    sendingDisabled
  }
`;

export const SUBACCOUNT_SUPERVOLUNTEER_FIELDS = gql`
  ${SUBACCOUNT_BASE_FIELDS}
  fragment SubaccountSupervolunteerFields on Subaccount {
    ...SubaccountBaseFields
    subscriberAudience {
      id
    }
    messagingStrategies {
      name
      isConfigured
      isEnabled
    }
    textingHoursTimezone
    textingHoursStart
    textingHoursEnd
    isWeText
  }
`;

export const SUBACCOUNT_ADMIN_FIELDS = gql`
  ${SUBACCOUNT_SUPERVOLUNTEER_FIELDS}
  fragment SubaccountAdminFields on Subaccount {
    ...SubaccountSupervolunteerFields
    createdAt
    texterAssignmentBatchSize
    texterAssignmentExpirationMinutes
    optOutMessage
    subscriberAudience {
      id
      shortCodeConfig {
        optInKeywords
        optInMessage
        optOutKeywords
        optOutMessage
        helpKeywords
        helpMessage
      }
    }
    ownedPhoneNumberPools {
      id
      name
      type
      tenantId
      createdAt
      archivedAt
    }
    linkShortenerDomains {
      id
      domain
    }
    defaultLinkShortenerDomainName
    budgetStatus {
      balanceInMillicents
      status
      orgOwners {
        id
        fullName
        email
      }
    }
    currentBudget {
      id
      startDate
      allocationInMillicents
      usageInMillicents
    }
    defaultMessagingStrategy {
      __typename
      strategy
      ... on CampaignMessagingStrategyBandwidth10Dlc {
        config {
          ownedPhoneNumber {
            id
            phoneNumber
          }
        }
      }
      ... on CampaignMessagingStrategyBandwidthTollFree {
        config {
          ownedPhoneNumber {
            id
            phoneNumber
          }
        }
      }
      ... on CampaignMessagingStrategyFake {
        config {
          _
        }
      }
      ... on CampaignMessagingStrategyBandwidth10DlcPool {
        config {
          ownedPhoneNumberPool {
            id
          }
        }
      }
      ... on CampaignMessagingStrategyBandwidthUnregisteredPool {
        config {
          ownedPhoneNumberPool {
            id
            name
          }
        }
      }
      ... on CampaignMessagingStrategySinch10Dlc {
        config {
          ownedPhoneNumber {
            id
            phoneNumber
          }
        }
      }
      ... on CampaignMessagingStrategyFakePool {
        config {
          ownedPhoneNumberPool {
            id
            name
          }
        }
      }
      ... on CampaignMessagingStrategyFakeShortCode {
        config {
          ownedPhoneNumber {
            id
            phoneNumber
          }
        }
      }
      ... on CampaignMessagingStrategyFake10Dlc {
        config {
          ownedPhoneNumber {
            id
            phoneNumber
          }
        }
      }
      ... on CampaignMessagingStrategyFakeTollFree {
        config {
          ownedPhoneNumber {
            id
            phoneNumber
          }
        }
      }
      ... on CampaignMessagingStrategyBandwidthShortCode {
        config {
          ownedPhoneNumber {
            id
            phoneNumber
          }
        }
      }
      ... on CampaignMessagingStrategyTwilioShortCode {
        config {
          ownedPhoneNumber {
            id
            phoneNumber
          }
        }
      }
    }
    texterSendEnabled
    donationHistoryPageEnabled

    responseScriptImportToSubaccountJob {
      args {
        sheetUrl
      }
      createdAt
      status
      errorMessage
      result {
        nGroupsCreated
        nRows
        nScriptsImported
        errors {
          msg
          row
        }
      }
    }
  }
`;

export const SUBACCOUNT_TENANT_OWNER_FIELDS = gql`
  ${SUBACCOUNT_ADMIN_FIELDS}
  fragment SubaccountTenantOwnerFields on Subaccount {
    ...SubaccountAdminFields
    settings {
      donationHistoryPage
      approvalsMode
    }
  }
`;

export const SUBACCOUNT_STAFF_FIELDS = gql`
  ${SUBACCOUNT_TENANT_OWNER_FIELDS}
  fragment SubaccountStaffFields on Subaccount {
    ...SubaccountTenantOwnerFields
  }
`;

export const LIST_SUBACCOUNTS_FOR_STAFF = gql`
  ${SUBACCOUNT_STAFF_FIELDS}
  query ListSubaccountsForStaff {
    listSubaccounts {
      ...SubaccountStaffFields
      isWeText
    }
  }
`;

export const LIST_MINIMAL_SUBACCOUNTS = gql`
  ${SUBACCOUNT_BASE_FIELDS}
  query ListMinimalSubaccounts {
    listSubaccounts {
      ...SubaccountBaseFields
    }
  }
`;

export const SET_SUBACCOUNT_IS_WE_TEXT = gql`
  mutation SetSubaccountIsWeText($input: SetSubaccountIsWeTextInput!) {
    setSubaccountIsWeText(input: $input) {
      id
      isWeText
    }
  }
`;

export const GET_SUBACCOUNT_BASE_FIELDS = gql`
  ${SUBACCOUNT_BASE_FIELDS}
  query getSubaccountBaseFields($input: GetSubaccountInput!) {
    getSubaccount(input: $input) {
      ...SubaccountBaseFields
    }
  }
`;

export const GET_SUBACCOUNT_SUPERVOLUNTEER_FIELDS = gql`
  ${SUBACCOUNT_SUPERVOLUNTEER_FIELDS}
  query getSubaccountSupervolunteerFields($input: GetSubaccountInput!) {
    getSubaccount(input: $input) {
      ...SubaccountSupervolunteerFields
    }
  }
`;

export const GET_SUBACCOUNT_ADMIN_FIELDS = gql`
  ${SUBACCOUNT_ADMIN_FIELDS}
  query getSubaccountAdminFields($input: GetSubaccountInput!) {
    getSubaccount(input: $input) {
      ...SubaccountAdminFields
    }
  }
`;

export const GET_SUBACCOUNT_METADATA_FIELDS = gql`
  ${METADATA_FIELDS_FRAGMENT}
  query getSubaccountMetadataFields($input: GetSubaccountInput!) {
    getSubaccount(input: $input) {
      id
      campaignMetadataFields {
        ...MetadataFields
      }
    }
  }
`;

export const GET_SUBACCOUNT_BUDGET_DATA = gql`
  query getSubaccountBudgetData($input: GetSubaccountInput!) {
    getSubaccount(input: $input) {
      currentBudget {
        id
        allocationInMillicents
        startDate
        usageInMillicents
      }
      organizationOwners {
        id
        fullName
        email
      }
    }
  }
`;

export const LIST_SUBACCOUNTS = gql`
  ${SUBACCOUNT_ADMIN_FIELDS}
  query ListSubaccounts {
    listSubaccounts {
      ...SubaccountAdminFields
    }
  }
`;

export const LIST_SUBACCOUNT_OWNER_FIELDS = gql`
  ${SUBACCOUNT_TENANT_OWNER_FIELDS}
  query ListSubaccountOwnerFields {
    listSubaccounts {
      ...SubaccountTenantOwnerFields
    }
  }
`;

export const CREATE_SUBACCOUNT = gql`
  ${SUBACCOUNT_ADMIN_FIELDS}

  mutation CreateSubaccount($subaccountInput: CreateSubaccountInput!) {
    createSubaccount(input: $subaccountInput) {
      ...SubaccountAdminFields
    }
  }
`;

export const EDIT_SUBACCOUNT_BASICS = gql`
  ${SUBACCOUNT_ADMIN_FIELDS}
  mutation EditSubaccountBasics($input: EditSubaccountBasicsInput!) {
    editSubaccountBasics(input: $input) {
      ...SubaccountAdminFields
    }
  }
`;

export const ARCHIVE_SUBACCOUNT = gql`
  ${SUBACCOUNT_ADMIN_FIELDS}
  mutation ArchiveSubaccount($input: ArchiveSubaccountInput!) {
    archiveSubaccount(input: $input) {
      ...SubaccountAdminFields
    }
  }
`;

export const UNARCHIVE_SUBACCOUNT = gql`
  ${SUBACCOUNT_ADMIN_FIELDS}
  mutation UnarchiveSubaccount($input: UnarchiveSubaccountInput!) {
    unarchiveSubaccount(input: $input) {
      ...SubaccountAdminFields
    }
  }
`;

export const EDIT_SUBACCOUNT_DEFAULT_MESSAGING_STRATEGY_BANDWIDTH_10DLC = gql`
  ${SUBACCOUNT_ADMIN_FIELDS}
  mutation EditSubaccountDefaultMessagingStrategyBandwidth10Dlc(
    $input: EditSubaccountDefaultMessagingStrategyBandwidth10DlcInput!
  ) {
    editSubaccountDefaultMessagingStrategyBandwidth10Dlc(input: $input) {
      ...SubaccountAdminFields
    }
  }
`;

export const EDIT_SUBACCOUNT_DEFAULT_MESSAGING_STRATEGY_BANDWIDTH_10DLC_POOL = gql`
  ${SUBACCOUNT_ADMIN_FIELDS}
  mutation EditSubaccountDefaultMessagingStrategyBandwidth10DlcPool(
    $input: EditSubaccountDefaultMessagingStrategyBandwidth10DlcPoolInput!
  ) {
    editSubaccountDefaultMessagingStrategyBandwidth10DlcPool(input: $input) {
      ...SubaccountAdminFields
    }
  }
`;

export const EDIT_SUBACCOUNT_DEFAULT_MESSAGING_STRATEGY_BANDWIDTH_SHORT_CODE = gql`
  ${SUBACCOUNT_ADMIN_FIELDS}
  mutation EditSubaccountDefaultMessagingStrategyBandwidthShortCode(
    $input: EditSubaccountDefaultMessagingStrategyBandwidthShortCodeInput!
  ) {
    editSubaccountDefaultMessagingStrategyBandwidthShortCode(input: $input) {
      ...SubaccountAdminFields
    }
  }
`;

export const EDIT_SUBACCOUNT_DEFAULT_MESSAGING_STRATEGY_BANDWIDTH_TOLL_FREE = gql`
  ${SUBACCOUNT_ADMIN_FIELDS}
  mutation EditSubaccountDefaultMessagingStrategyBandwidthTollFree(
    $input: EditSubaccountDefaultMessagingStrategyBandwidthTollFreeInput!
  ) {
    editSubaccountDefaultMessagingStrategyBandwidthTollFree(input: $input) {
      ...SubaccountAdminFields
    }
  }
`;

export const EDIT_SUBACCOUNT_DEFAULT_MESSAGING_STRATEGY_BANDWIDTH_UNREGISTERED_POOL = gql`
  ${SUBACCOUNT_ADMIN_FIELDS}
  mutation EditSubaccountDefaultMessagingStrategyBandwidthUnregisteredPool(
    $input: EditSubaccountDefaultMessagingStrategyBandwidthUnregisteredPoolInput!
  ) {
    editSubaccountDefaultMessagingStrategyBandwidthUnregisteredPool(
      input: $input
    ) {
      ...SubaccountAdminFields
    }
  }
`;

export const EDIT_SUBACCOUNT_DEFAULT_MESSAGING_STRATEGY_SINCH_10DLC = gql`
  ${SUBACCOUNT_ADMIN_FIELDS}
  mutation EditSubaccountDefaultMessagingStrategySinch10Dlc(
    $input: EditSubaccountDefaultMessagingStrategySinch10DlcInput!
  ) {
    editSubaccountDefaultMessagingStrategySinch10Dlc(input: $input) {
      ...SubaccountAdminFields
    }
  }
`;

export const EDIT_SUBACCOUNT_DEFAULT_MESSAGING_STRATEGY_FAKE_POOL = gql`
  ${SUBACCOUNT_ADMIN_FIELDS}
  mutation EditSubaccountDefaultMessagingStrategyFakePool(
    $input: EditSubaccountDefaultMessagingStrategyFakePoolInput!
  ) {
    editSubaccountDefaultMessagingStrategyFakePool(input: $input) {
      ...SubaccountAdminFields
    }
  }
`;

export const EDIT_SUBACCOUNT_DEFAULT_MESSAGING_STRATEGY_FAKE_SHORT_CODE = gql`
  ${SUBACCOUNT_ADMIN_FIELDS}
  mutation EditSubaccountDefaultMessagingStrategyFakeShortCode(
    $input: EditSubaccountDefaultMessagingStrategyFakeShortCodeInput!
  ) {
    editSubaccountDefaultMessagingStrategyFakeShortCode(input: $input) {
      ...SubaccountAdminFields
    }
  }
`;

export const EDIT_SUBACCOUNT_DEFAULT_MESSAGING_STRATEGY_FAKE_10DLC = gql`
  ${SUBACCOUNT_ADMIN_FIELDS}
  mutation EditSubaccountDefaultMessagingStrategyFake10Dlc(
    $input: EditSubaccountDefaultMessagingStrategyFake10DlcInput!
  ) {
    editSubaccountDefaultMessagingStrategyFake10Dlc(input: $input) {
      ...SubaccountAdminFields
    }
  }
`;

export const EDIT_SUBACCOUNT_DEFAULT_MESSAGING_STRATEGY_FAKE_TOLL_FREE = gql`
  ${SUBACCOUNT_ADMIN_FIELDS}
  mutation EditSubaccountDefaultMessagingStrategyFakeTollFree(
    $input: EditSubaccountDefaultMessagingStrategyFakeTollFreeInput!
  ) {
    editSubaccountDefaultMessagingStrategyFakeTollFree(input: $input) {
      ...SubaccountAdminFields
    }
  }
`;

export const EDIT_SUBACCOUNT_DEFAULT_MESSAGING_TWILIO_SHORT_CODE = gql`
  ${SUBACCOUNT_ADMIN_FIELDS}
  mutation EditSubaccountDefaultMessagingStrategyTwilioShortCode(
    $input: EditSubaccountDefaultMessagingStrategyTwilioShortCodeInput!
  ) {
    editSubaccountDefaultMessagingStrategyTwilioShortCode(input: $input) {
      ...SubaccountAdminFields
    }
  }
`;

export const BULK_UPDATE_SUBACCOUNT_SETTINGS = gql`
  ${SUBACCOUNT_TENANT_OWNER_FIELDS}
  mutation BulkUpdateSubaccountSettings(
    $input: BulkUpdateSubaccountSettingsInput!
  ) {
    bulkUpdateSubaccountSettings(input: $input) {
      ...SubaccountTenantOwnerFields
    }
  }
`;
